import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, UserCheck } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-page">
      <Header />

      <div className="pt-32 pb-20 bg-slate-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold mb-8"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-slate-900">Privacy Policy</h1>
                <p className="text-sm text-slate-500 mt-1">Last Updated: January 2026</p>
              </div>
            </div>

            <div className="space-y-8 text-slate-700">
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Commitment to Privacy</h2>
                <p className="leading-relaxed">
                  At Ashvya Digital Media, we are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services or visit our website.
                </p>
              </section>

              <section>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Eye className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">Personal Information</h3>
                <p className="leading-relaxed mb-4">
                  We may collect personal information that you voluntarily provide to us, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Name and contact details (email, phone number)</li>
                  <li>Business information (company name, industry, website)</li>
                  <li>Payment and billing information</li>
                  <li>Communication preferences</li>
                  <li>Information you provide in forms, surveys, or consultations</li>
                </ul>

                <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">Automatically Collected Information</h3>
                <p className="leading-relaxed mb-4">
                  When you visit our website, we may automatically collect:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Browser type and version</li>
                  <li>IP address and location data</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website</li>
                  <li>Device information</li>
                </ul>
              </section>

              <section>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
                  </div>
                </div>
                
                <p className="leading-relaxed mb-4">
                  We use the collected information for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Delivery:</strong> To provide and manage our digital marketing services</li>
                  <li><strong>Communication:</strong> To respond to inquiries, send updates, and provide customer support</li>
                  <li><strong>Improvement:</strong> To analyze and improve our services and website</li>
                  <li><strong>Marketing:</strong> To send newsletters, promotions, and marketing materials (with your consent)</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
                  <li><strong>Analytics:</strong> To understand how visitors use our website and optimize user experience</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information Sharing and Disclosure</h2>
                <p className="leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Providers:</strong> Third-party platforms necessary for service delivery (e.g., Google Ads, Facebook, email services)</li>
                  <li><strong>Business Partners:</strong> With your consent, for collaborative services</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale</li>
                </ul>
              </section>

              <section>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
                  </div>
                </div>
                
                <p className="leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures to protect your information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Secure SSL encryption for data transmission</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security assessments</li>
                  <li>Employee training on data protection</li>
                  <li>Secure storage and backup systems</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but will notify you of any breach as required by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Cookies and Tracking Technologies</h2>
                <p className="leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Remember your preferences</li>
                  <li>Understand website usage patterns</li>
                  <li>Deliver targeted advertising</li>
                  <li>Improve website functionality</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  You can control cookies through your browser settings. Disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Privacy Rights</h2>
                <p className="leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your information (subject to legal obligations)</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Data Portability:</strong> Request transfer of your data</li>
                  <li><strong>Object:</strong> Object to certain processing activities</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  To exercise these rights, contact us at ashvyadigitalmedia@gmail.com
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
                <p className="leading-relaxed">
                  We retain your information for as long as necessary to provide services and comply with legal obligations. When no longer needed, we securely delete or anonymize your data.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Third-Party Links</h2>
                <p className="leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these sites. We encourage you to review their privacy policies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Children's Privacy</h2>
                <p className="leading-relaxed">
                  Our services are not directed to individuals under 18. We do not knowingly collect information from children. If we become aware of such collection, we will promptly delete it.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Changes to This Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of our services after changes constitutes acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Contact Us</h2>
                <p className="leading-relaxed mb-4">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-6">
                  <p className="font-bold text-lg text-slate-900 mb-3">Ashvya Digital Media</p>
                  <div className="space-y-2 text-slate-700">
                    <p><strong>Address:</strong> Modinagar, Ghaziabad, Uttar Pradesh</p>
                    <p><strong>Email:</strong> <a href="mailto:ashvyadigitalmedia@gmail.com" className="text-teal-600 hover:text-teal-700">ashvyadigitalmedia@gmail.com</a></p>
                    <p><strong>Phone:</strong> <a href="tel:9068922387" className="text-teal-600 hover:text-teal-700">9068922387</a></p>
                  </div>
                </div>
              </section>

              <section className="bg-slate-100 rounded-2xl p-6 mt-8">
                <p className="text-sm text-slate-600 italic">
                  By using our services, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;